import React, { useState, useMemo, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Image, Mic, FileText, Trophy, Calendar, Trash2 } from 'lucide-react';
import { PROOF_TYPES } from '../hooks/useTaskProofs';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';
import { haptic } from '../../../shared/utils/haptics';

// Category colors
const CATEGORY_COLORS = {
  vital: 'bg-red-500/20 text-red-400 border-red-500/30',
  chore: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  pleasure: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  reward: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  default: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
};

/**
 * VictoryGallery - Visual journal of accomplishments
 */
export const VictoryGallery = memo(function VictoryGallery({ proofs, onClose, onDelete }) {
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('week');
  const [selectedProof, setSelectedProof] = useState(null);

  // Filter proofs
  const filteredProofs = useMemo(() => {
    let filtered = [...proofs];

    // Type filter
    if (filter !== 'all') {
      filtered = filtered.filter(p => {
        if (filter === 'photo') return p.type === PROOF_TYPES.PHOTO || p.type === PROOF_TYPES.SCREENSHOT;
        if (filter === 'text') return p.type === PROOF_TYPES.TEXT;
        if (filter === 'voice') return p.type === PROOF_TYPES.VOICE;
        return true;
      });
    }

    // Date filter
    const now = new Date();
    if (dateFilter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(p => new Date(p.createdAt) >= weekAgo);
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(p => new Date(p.createdAt) >= monthAgo);
    }

    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [proofs, filter, dateFilter]);

  // Group by date
  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredProofs.forEach(proof => {
      const date = proof.date || proof.createdAt.split('T')[0];
      if (!groups[date]) groups[date] = [];
      groups[date].push(proof);
    });
    return groups;
  }, [filteredProofs]);

  // Stats
  const stats = useMemo(() => ({
    total: filteredProofs.length,
    photos: filteredProofs.filter(p => p.type === PROOF_TYPES.PHOTO || p.type === PROOF_TYPES.SCREENSHOT).length,
    texts: filteredProofs.filter(p => p.type === PROOF_TYPES.TEXT).length,
    voices: filteredProofs.filter(p => p.type === PROOF_TYPES.VOICE).length
  }), [filteredProofs]);

  // Format date
  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

    if (dateStr === today.toISOString().split('T')[0]) return "Aujourd'hui";
    if (dateStr === yesterday.toISOString().split('T')[0]) return "Hier";

    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  }, []);

  // Get type icon
  const getTypeIcon = useCallback((type) => {
    switch (type) {
      case PROOF_TYPES.PHOTO: return Camera;
      case PROOF_TYPES.SCREENSHOT: return Image;
      case PROOF_TYPES.VOICE: return Mic;
      case PROOF_TYPES.TEXT: return FileText;
      default: return Trophy;
    }
  }, []);

  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  const handleFilterChange = useCallback((newFilter) => {
    haptic.tap();
    setFilter(newFilter);
  }, []);

  const handleDateFilterChange = useCallback((newFilter) => {
    haptic.tap();
    setDateFilter(newFilter);
  }, []);

  return (
    <motion.div
      className={cn('fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2', Z_CLASS.VICTORY_GALLERY)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-elevated w-full max-w-lg max-h-[95svh] flex flex-col overflow-y-auto"
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="p-4 flex-shrink-0 border-b border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 glass-subtle rounded-lg flex items-center justify-center">
                <Trophy className="w-4 h-4 text-amber-400" />
              </div>
              <h2 className="font-medium text-base text-zinc-100">Galerie des Victoires</h2>
            </div>
            <motion.button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <X className="w-4 h-4 text-zinc-400" />
            </motion.button>
          </div>

          {/* Quick stats */}
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="font-medium text-zinc-200">{stats.total}</span>
              <span className="text-zinc-500">preuves</span>
            </div>
            <div className="flex items-center gap-1">
              <Camera className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-zinc-400">{stats.photos}</span>
            </div>
            <div className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-zinc-400">{stats.texts}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-white/[0.06] flex gap-2 flex-wrap flex-shrink-0">
          {/* Type filter */}
          <div className="flex gap-1">
            {[
              { id: 'all', label: 'Tout' },
              { id: 'photo', label: 'Photos', icon: Camera },
              { id: 'text', label: 'Notes', icon: FileText }
            ].map(f => (
              <motion.button
                key={f.id}
                onClick={() => handleFilterChange(f.id)}
                className={cn(
                  'px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors',
                  filter === f.id
                    ? 'bg-zinc-700 text-zinc-100'
                    : 'glass-subtle text-zinc-500 hover:text-zinc-400'
                )}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {f.icon && <f.icon className="w-3 h-3" />}
                {f.label}
              </motion.button>
            ))}
          </div>

          {/* Date filter */}
          <div className="flex gap-1 ml-auto">
            {[
              { id: 'week', label: '7j' },
              { id: 'month', label: '30j' },
              { id: 'all', label: 'Tout' }
            ].map(f => (
              <motion.button
                key={f.id}
                onClick={() => handleDateFilterChange(f.id)}
                className={cn(
                  'px-2 py-1 rounded-lg text-xs font-medium transition-colors',
                  dateFilter === f.id
                    ? 'bg-zinc-700 text-zinc-100'
                    : 'glass-subtle text-zinc-500 hover:text-zinc-400'
                )}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {f.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-3">
          {filteredProofs.length === 0 ? (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-16 h-16 mx-auto glass-card flex items-center justify-center mb-4">
                <Trophy className="w-8 h-8 text-zinc-600" />
              </div>
              <p className="text-zinc-400 font-medium text-sm">Aucune victoire pour le moment</p>
              <p className="text-zinc-600 text-xs mt-1">
                Complète des tâches et ajoute des preuves !
              </p>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedByDate).map(([date, dateProofs], groupIndex) => (
                <motion.div
                  key={date}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.1 }}
                >
                  {/* Date header */}
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                    <h3 className="text-xs font-medium text-zinc-400 capitalize">
                      {formatDate(date)}
                    </h3>
                    <span className="text-[10px] text-zinc-600">
                      ({dateProofs.length} victoire{dateProofs.length > 1 ? 's' : ''})
                    </span>
                  </div>

                  {/* Proofs grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {dateProofs.map((proof, index) => {
                      const Icon = getTypeIcon(proof.type);
                      const isImage = proof.type === PROOF_TYPES.PHOTO || proof.type === PROOF_TYPES.SCREENSHOT;
                      const categoryColor = CATEGORY_COLORS[proof.category] || CATEGORY_COLORS.default;

                      return (
                        <motion.div
                          key={proof.id}
                          onClick={() => { haptic.tap(); setSelectedProof(proof); }}
                          className="glass-card overflow-hidden cursor-pointer"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {/* Image or icon */}
                          {isImage && proof.data ? (
                            <img
                              src={proof.data}
                              alt={proof.taskName}
                              className="w-full h-24 object-cover"
                            />
                          ) : (
                            <div className={cn('w-full h-24 flex items-center justify-center', categoryColor)}>
                              <Icon className="w-8 h-8" />
                            </div>
                          )}

                          {/* Info */}
                          <div className="p-2">
                            <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium', categoryColor)}>
                              {proof.category || 'tâche'}
                            </span>
                            <p className="text-xs text-zinc-300 mt-1 line-clamp-1">{proof.taskName}</p>
                            {proof.type === PROOF_TYPES.TEXT && (
                              <p className="text-[10px] text-zinc-500 mt-0.5 line-clamp-2">{proof.data}</p>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/[0.06] flex-shrink-0">
          <p className="text-center text-xs text-zinc-600">
            Chaque preuve est une victoire.
          </p>
        </div>
      </motion.div>

      {/* Detail modal */}
      <AnimatePresence>
        {selectedProof && (
          <ProofDetailModal
            proof={selectedProof}
            onClose={() => setSelectedProof(null)}
            onDelete={onDelete}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
});

/**
 * ProofDetailModal - Detail view for a proof
 */
const ProofDetailModal = memo(function ProofDetailModal({ proof, onClose, onDelete }) {
  const isImage = proof.type === PROOF_TYPES.PHOTO || proof.type === PROOF_TYPES.SCREENSHOT;
  const Icon = proof.type === PROOF_TYPES.VOICE ? Mic :
               proof.type === PROOF_TYPES.TEXT ? FileText : Camera;

  const handleDelete = useCallback(() => {
    if (window.confirm('Supprimer cette preuve ?')) {
      haptic.tap();
      onDelete(proof.id);
      onClose();
    }
  }, [onDelete, proof.id, onClose]);

  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  return (
    <motion.div
      className={cn('fixed inset-0 bg-black/80 flex items-center justify-center p-4', Z_CLASS.VICTORY_GALLERY_DETAIL)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-elevated w-full max-w-md max-h-[95svh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Image or content */}
        {isImage && proof.data ? (
          <img
            src={proof.data}
            alt={proof.taskName}
            className="w-full max-h-[50vh] object-contain bg-black"
          />
        ) : (
          <div className="p-8 text-center glass-card">
            <Icon className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            {proof.type === PROOF_TYPES.TEXT && (
              <p className="text-zinc-300 text-sm">{proof.data}</p>
            )}
          </div>
        )}

        {/* Info */}
        <div className="p-4">
          <h3 className="font-medium text-sm text-zinc-100">{proof.taskName}</h3>
          <p className="text-xs text-zinc-500 mt-1">
            {new Date(proof.createdAt).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>

          <div className="flex gap-2 mt-4">
            <motion.button
              onClick={handleClose}
              className="flex-1 py-2.5 px-4 glass-card text-zinc-300 font-medium text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Fermer
            </motion.button>
            <motion.button
              onClick={handleDelete}
              className="py-2.5 px-4 bg-red-500/10 text-red-400 rounded-xl font-medium text-sm flex items-center gap-1"
              whileHover={{ scale: 1.02, backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <Trash2 className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
});

export default VictoryGallery;
